<template>
  <div class="toc-wrapper">
    <div
      class="table-of-contents-wrapper"
      :class="{ opened }"
    >
      <ProgressBar
        v-if="!opened"
        ref="progress-bar"
        :top-element="topElement"
        :bottom-element="bottomElement"
      />
      <div
        v-if="showToc"
        ref="toc"
        class="table-of-contents hidden"
      >
        <p
          class="mobile-header"
          @click="opened = !opened"
        >
          <span>{{ selectedSection || 'Table of contents' }}</span>
          <ChevronDownIcon />
        </p>
        <ul
          class="list"
          :class="{ 'with-hire-button': !!buttonText }"
        >
          <li
            v-for="section in toc"
            :key="section.anchor"
          >
            <a
              :class="{ active: section.content === selectedSection }"
              @click="sendToAnchor(section.anchor, section.content)"
            >
              {{ section.content }}
            </a>
            <ul
              v-if="section?.subcontent?.length"
              class="post-links"
            >
              <li
                v-for="link in section.subcontent"
                :key="link.anchor"
              >
                <a
                  :class="{ active: link.content === selectedSection }"
                  @click="sendToAnchor(link.anchor, link.content)"
                >
                  {{ link.content }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from '~/components/Common/ProgressBar/ProgressBar.vue';
import ChevronDownIcon from '~/components/Common/Svg/ChevronDownIconV2.vue';
import debounce from '~/utils/helpers/debounce.js';

export default {
  components: {
    ProgressBar,
    ChevronDownIcon,
  },
  props: {
    toc: {
      type: Array,
      default: () => [],
    },
    buttonText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      opened: false,
      observer: null,
      selectedSection: '',
      tabletWidth: 1000,
    };
  },
  computed: {
    sections() {
      const sectionsObject = {};
      this.toc.forEach((link) => {
        sectionsObject[`#${link.anchor}`] = link.content;
        if (link?.subcontent?.length) {
          link.subcontent.forEach((link) => {
            sectionsObject[`#${link.anchor}`] = link.content;
          });
        }
      });

      return sectionsObject;
    },
    topElement() {
      return this.showToc ? `#${this.toc[0].anchor}` : '';
    },
    bottomElement() {
      return this.showToc ? `#${this.toc[this.toc.length - 1].anchor}` : '';
    },
    showToc() {
      return !!this.toc.length;
    },
  },
  watch: {
    $route() {
      this.opened = false;
      this.selectedSection = this.sections[this.$route.hash];
    },
  },
  mounted() {
    const windowInnerWidth = window.innerWidth;

    if (windowInnerWidth > this.tabletWidth && this.showToc) {
      this.$refs.toc.classList.remove('hidden');
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = `#${entry.target.id}`;

        if (entry.isIntersecting) {
          if (windowInnerWidth <= this.tabletWidth) {
            this.$refs.toc.classList.remove('hidden');
          }
        } else if (
          (id === this.topElement || id === this.bottomElement) &&
          this.selectedSection === this.sections[id]
        ) {
          if (windowInnerWidth <= this.tabletWidth) {
            this.$refs.toc.classList.add('hidden');
          }
        }
      });
    });

    this.setObserver();
    window.addEventListener('scroll', debounce(this.handleScrollStop, 200));
  },
  beforeUnmount() {
    this.setObserver(false);
    window.removeEventListener('scroll', debounce(this.handleScrollStop, 200));
  },
  methods: {
    setObserver(observe = true) {
      Object.keys(this.sections).forEach((id) => {
        const element = document.getElementById(id.substring(1));
        if (element) {
          if (observe) {
            this.observer.observe(element);
          } else {
            this.observer.unobserve(element);
          }
        }
      });
    },
    handleScrollStop() {
      this.setObserver(false);
      this.$nextTick(() => {
        this.setObserver();
      });
    },
    sendToAnchor(id, title = null) {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState({}, null, `${this.$route.path}#${id}`);
        this.selectedSection = title;
      }

      if (this.opened) {
        this.opened = !this.opened;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './TableOfContents.scss';
</style>
